import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Avatar,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Email,
  Cloud,
  Security,
  Notifications,
  Business,
  Save,
  Upload,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { settingsAPI } from "../services/api";
import { useSnackbar } from "notistack";
import { useTenant } from "../contexts/TenantContext";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const SettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentTenant, updateTenantSettings } = useTenant();

  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data.settings || {});
    } catch (error) {
      enqueueSnackbar("Failed to load settings", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Company Settings Form
  const companyFormik = useFormik({
    initialValues: {
      companyName: currentTenant?.name || "",
      companyLogo: "",
      timezone: "Asia/Kolkata",
      currency: "INR",
      dateFormat: "DD/MM/YYYY",
      workingHours: {
        start: "09:00",
        end: "18:00",
        breakDuration: 60,
      },
      complianceState: currentTenant?.settings?.complianceState || "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Company name is required"),
      complianceState: Yup.string().required("Compliance state is required"),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        await updateTenantSettings({ company: values });
        enqueueSnackbar("Company settings saved successfully", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar("Failed to save settings", { variant: "error" });
      } finally {
        setSaving(false);
      }
    },
  });

  // SMTP Settings Form
  const smtpFormik = useFormik({
    initialValues: {
      provider: settings.smtp?.provider || "gmail",
      host: settings.smtp?.host || "",
      port: settings.smtp?.port || 587,
      username: settings.smtp?.username || "",
      password: settings.smtp?.password || "",
      secure: settings.smtp?.secure || false,
      fromEmail: settings.smtp?.fromEmail || "",
      fromName: settings.smtp?.fromName || "",
    },
    validationSchema: Yup.object({
      host: Yup.string().required("SMTP host is required"),
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required"),
      fromEmail: Yup.string()
        .email("Invalid email")
        .required("From email is required"),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        await settingsAPI.update({ smtp: values });
        enqueueSnackbar("SMTP settings saved successfully", {
          variant: "success",
        });
        loadSettings();
      } catch (error) {
        enqueueSnackbar("Failed to save SMTP settings", { variant: "error" });
      } finally {
        setSaving(false);
      }
    },
  });

  const handleTestSMTP = async () => {
    setTestingConnection(true);
    try {
      await settingsAPI.testSMTP(smtpFormik.values);
      enqueueSnackbar("SMTP connection successful!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("SMTP connection failed", { variant: "error" });
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Settings
      </Typography>

      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Business />} label="Company" />
          <Tab icon={<Email />} label="Email/SMTP" />
          <Tab icon={<Cloud />} label="Cloud Storage" />
          <Tab icon={<Notifications />} label="Notifications" />
          <Tab icon={<Security />} label="Security" />
        </Tabs>

        {/* Company Settings */}
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={companyFormik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader title="Company Information" />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="companyName"
                          label="Company Name"
                          value={companyFormik.values.companyName}
                          onChange={companyFormik.handleChange}
                          error={
                            companyFormik.touched.companyName &&
                            Boolean(companyFormik.errors.companyName)
                          }
                          helperText={
                            companyFormik.touched.companyName &&
                            companyFormik.errors.companyName
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          name="timezone"
                          label="Timezone"
                          value={companyFormik.values.timezone}
                          onChange={companyFormik.handleChange}
                          SelectProps={{ native: true }}
                        >
                          <option value="Asia/Kolkata">
                            Asia/Kolkata (IST)
                          </option>
                          <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                          <option value="UTC">UTC</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          name="complianceState"
                          label="Primary Compliance State"
                          value={companyFormik.values.complianceState}
                          onChange={companyFormik.handleChange}
                          SelectProps={{ native: true }}
                        >
                          <option value="">Select State</option>
                          <option value="Karnataka">Karnataka</option>
                          <option value="Maharashtra">Maharashtra</option>
                          <option value="Tamil Nadu">Tamil Nadu</option>
                          <option value="Gujarat">Gujarat</option>
                          <option value="Delhi">Delhi</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          name="workingHours.start"
                          label="Work Start Time"
                          type="time"
                          value={companyFormik.values.workingHours.start}
                          onChange={companyFormik.handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          name="workingHours.end"
                          label="Work End Time"
                          type="time"
                          value={companyFormik.values.workingHours.end}
                          onChange={companyFormik.handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          name="workingHours.breakDuration"
                          label="Break Duration (minutes)"
                          type="number"
                          value={
                            companyFormik.values.workingHours.breakDuration
                          }
                          onChange={companyFormik.handleChange}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader title="Company Logo" />
                  <CardContent sx={{ textAlign: "center" }}>
                    <Avatar
                      sx={{ width: 100, height: 100, margin: "0 auto", mb: 2 }}
                      src={companyFormik.values.companyLogo}
                    >
                      {companyFormik.values.companyName?.[0]?.toUpperCase()}
                    </Avatar>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Upload />}
                      sx={{ mb: 1 }}
                    >
                      Upload Logo
                      <input type="file" hidden accept="image/*" />
                    </Button>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Recommended: 200x200px, PNG/JPG
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saving}
                  sx={{ mr: 2 }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {/* SMTP Settings */}
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={smtpFormik.handleSubmit}>
            <Card>
              <CardHeader
                title="Email Configuration"
                subheader="Configure SMTP settings for sending emails"
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      name="provider"
                      label="Email Provider"
                      value={smtpFormik.values.provider}
                      onChange={smtpFormik.handleChange}
                      SelectProps={{ native: true }}
                    >
                      <option value="gmail">Gmail</option>
                      <option value="outlook">Outlook</option>
                      <option value="sendgrid">SendGrid</option>
                      <option value="custom">Custom SMTP</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="host"
                      label="SMTP Host"
                      value={smtpFormik.values.host}
                      onChange={smtpFormik.handleChange}
                      error={
                        smtpFormik.touched.host &&
                        Boolean(smtpFormik.errors.host)
                      }
                      helperText={
                        smtpFormik.touched.host && smtpFormik.errors.host
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="port"
                      label="Port"
                      type="number"
                      value={smtpFormik.values.port}
                      onChange={smtpFormik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="secure"
                          checked={smtpFormik.values.secure}
                          onChange={smtpFormik.handleChange}
                        />
                      }
                      label="Use SSL/TLS"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="username"
                      label="Username"
                      value={smtpFormik.values.username}
                      onChange={smtpFormik.handleChange}
                      error={
                        smtpFormik.touched.username &&
                        Boolean(smtpFormik.errors.username)
                      }
                      helperText={
                        smtpFormik.touched.username &&
                        smtpFormik.errors.username
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      value={smtpFormik.values.password}
                      onChange={smtpFormik.handleChange}
                      error={
                        smtpFormik.touched.password &&
                        Boolean(smtpFormik.errors.password)
                      }
                      helperText={
                        smtpFormik.touched.password &&
                        smtpFormik.errors.password
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="fromEmail"
                      label="From Email"
                      value={smtpFormik.values.fromEmail}
                      onChange={smtpFormik.handleChange}
                      error={
                        smtpFormik.touched.fromEmail &&
                        Boolean(smtpFormik.errors.fromEmail)
                      }
                      helperText={
                        smtpFormik.touched.fromEmail &&
                        smtpFormik.errors.fromEmail
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="fromName"
                      label="From Name"
                      value={smtpFormik.values.fromName}
                      onChange={smtpFormik.handleChange}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save SMTP Settings"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={handleTestSMTP}
                    disabled={testingConnection}
                  >
                    {testingConnection ? "Testing..." : "Test Connection"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </form>
        </TabPanel>

        {/* Cloud Storage Settings */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Google Drive Integration" />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Connect your Google Drive for automated document backup
                  </Alert>
                  <TextField
                    fullWidth
                    label="Client ID"
                    placeholder="Google Drive Client ID"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Client Secret"
                    type="password"
                    placeholder="Google Drive Client Secret"
                    sx={{ mb: 2 }}
                  />
                  <Button variant="contained" fullWidth>
                    Connect Google Drive
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="OneDrive Integration" />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Connect your OneDrive for additional backup storage
                  </Alert>
                  <TextField
                    fullWidth
                    label="Application ID"
                    placeholder="Microsoft App ID"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Client Secret"
                    type="password"
                    placeholder="Microsoft Client Secret"
                    sx={{ mb: 2 }}
                  />
                  <Button variant="contained" fullWidth>
                    Connect OneDrive
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardHeader title="Notification Preferences" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Email Notifications
                  </Typography>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Worker registration notifications"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Compliance deadline reminders"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Daily attendance summaries"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Weekly reports"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Push Notifications
                  </Typography>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Real-time attendance alerts"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="System maintenance notifications"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Emergency notifications"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Password Policy" />
                <CardContent>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Require strong passwords"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Force password change every 90 days"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Two-factor authentication"
                  />
                  <TextField
                    fullWidth
                    label="Session timeout (minutes)"
                    type="number"
                    defaultValue={30}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Access Control" />
                <CardContent>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="IP-based access restrictions"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Device-based authentication"
                  />
                  <TextField
                    fullWidth
                    label="Maximum login attempts"
                    type="number"
                    defaultValue={5}
                    sx={{ mt: 2, mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Account lockout duration (minutes)"
                    type="number"
                    defaultValue={30}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
